import type { ButtonProps, ChakraProps } from "@chakra-ui/react"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { addMembershipById, addPromoCode, requestInProgress } from "lib/api"
import React from "react"
import { colors } from "lib/theme"
import type { BannerCTA } from "types/cms"
import type { CMSPageProps } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { loadZendeskScript } from "utils/loadZendeskScript"
import { ButtonModel, PrimaryBtnCopy, SecondaryBtnCopy } from "./styles"
import { colours } from "@shared/ui.design-system"

const colorMap = {
  Pink: colours.accent.soft,
  Black: colours.primary.dark,
  White: colours.secondary.light,
  Red: colors.contentRed,
  "Red/Black": colors.contentRed,
}

const backgroundColorToTextColorMap = {
  Pink: colors.content,
  Black: colors.contentInverse,
  White: colors.content,
  Red: colors.contentInverse,
  "Red/Black": colors.content,
}

type Props = BannerCTA &
  CMSPageProps & {
    customOnClickHandler?: (e) => void
    isLoading?: boolean
    buttonProps?: ButtonProps
    buttonCopyProps?: ChakraProps
  }

export const Button: React.FC<Props> = ({
  trackingModule,
  title,
  link,
  backgroundColor = "Black",
  customOnClickHandler,
  isLoading = false,
  buttonProps = {},
  buttonCopyProps = {},
  discount,
  membershipId,
  isPrimaryStyle = true,
}) => {
  const { setATBConfirmation } = useLandingPageContext()
  const { modulePosition } = trackingModule

  const handleClick = () => {
    cmsPageClick({
      ...trackingModule,
      moduleContentUrl: link,
      moduleContentDetails: "cta_clicked",
    })

    if (link === "atb" && membershipId) {
      addMembershipById(membershipId).then(() => {
        if (discount) addPromoCode(discount)
        setATBConfirmation(true)
        requestInProgress(false)
      })
      return
    }

    if (link === "chatbot") {
      loadZendeskScript()
    }
  }

  const clickHandler = customOnClickHandler ?? handleClick

  return (
    <ButtonModel
      onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        clickHandler(e)
      }}
      isLoading={isLoading}
      textColor={backgroundColorToTextColorMap[backgroundColor]}
      css={{
        backgroundColor: colorMap[backgroundColor],
        "&:hover,&:focus": {
          backgroundColor: colorMap[backgroundColor],
        },
      }}
      border={
        colorMap[backgroundColor] === colours.secondary.light
          ? `1px solid ${colors.content}`
          : "none"
      }
      {...buttonProps}
    >
      {modulePosition === 1 && isPrimaryStyle ? (
        <PrimaryBtnCopy {...buttonCopyProps}>{title}</PrimaryBtnCopy>
      ) : (
        <SecondaryBtnCopy {...buttonCopyProps}>{title}</SecondaryBtnCopy>
      )}
    </ButtonModel>
  )
}
